import { Button, Checkbox, Grid, Typography } from '@mui/material';
import { concat } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardGrid } from 'src/components/cardGrid';
import { FilterList } from 'src/components/filterList';
import { Footer } from 'src/components/footer/Footer';
import { Header } from 'src/components/header';
import { InputTextLabel } from 'src/components/input/Input.styled';
import { SimpleDialog } from 'src/components/modal';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress';
import { Snackbar } from 'src/components/snackbar';
import { StyledTypography } from 'src/components/subHeader/SubHeaderWithSorting.styled';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import { PATHS } from 'src/constants/paths';
import { useFilterAndSorting, useModal } from 'src/hooks';
import { IComponent, IFilter, IStatus, IStatusLabel } from 'src/interfaces';
import {
  DataTestID,
  getComponentDisplayName,
  lang,
  useUserRole,
} from 'src/utils';

import {
  CardGridWrapper,
  CheckboxLabel,
  ComponentContainer,
  ComponentSectionHeader,
  InputContainer,
  InputTextField,
  StyleDivider,
} from './AddComponentTagPage.styled';
import { ALLOWED_REGEX, tagNameMaxLength, TestIds } from './constants';
import { useCreateComponentTagPage } from './hooks/AddComponentTagPage.hook';

export const AddComponentTagPage = () => {
  const [inputBoxStatus, setInputBoxStatus] = React.useState('');
  const [componentTagName, setComponentTagName] = React.useState('');
  const [selectedComponents, setSelectedComponents] = React.useState<
    IComponent[]
  >([]);
  const gridBottomRef = useRef<HTMLDivElement>(null);
  const [selectAll, setSelectAll] = React.useState(false);

  const {
    data,
    isLoadingComponents,
    getNextComponentPage,
    errorLoadingComponents,
    hasNextPage,
    fetchNextPage,
    componentTypesPage,
    isLoadingComponentTypes,
    errorLoadingComponentTypes,
    onChangeParams,
    createComponentTag,
    updateComponent,
  } = useCreateComponentTagPage();
  const navigate = useNavigate();
  const CloseModal = useModal();
  const { isViewOnly } = useUserRole();

  const components = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  const selectedCount = selectedComponents.length;

  useEffect(() => {
    if (components.length && gridBottomRef.current) {
      const { bottom } = gridBottomRef.current.getBoundingClientRect();
      const shouldInstantlyFetchNextPage =
        bottom < window.innerHeight && hasNextPage;

      if (shouldInstantlyFetchNextPage) {
        fetchNextPage();
      }
      setSelectAll(areAllComponentsSelected(components, selectedComponents));
    }
  }, [components, fetchNextPage, hasNextPage, selectedComponents]);

  const filters: IFilter[] = [
    {
      label: 'Component type',
      options: componentTypesPage?.data.map((componentType) => ({
        value: componentType.id,
        label: getComponentDisplayName(componentType.name),
      })),
      totalPages: componentTypesPage?.totalPages,
      currPage: componentTypesPage.page,
      isLoading: isLoadingComponentTypes,
      isError: errorLoadingComponentTypes ? true : false,
    },
    {
      label: 'Status',
      options: Object.values(IStatus).map((value) => ({
        value: value,
        label: IStatusLabel[`${value}`],
      })),
    },
    {
      label: lang.componentTags,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'primary',
      },
    },
  ];

  const { onChangeKeyword, onRemoveKeyword, onReset } =
    useFilterAndSorting(filters);

  const handleSelectAllClick = () => {
    if (selectAll) {
      setSelectedComponents([]);
    } else {
      setSelectedComponents(components);
    }
    setSelectAll(!selectAll);
  };

  const handleComponentTagNameChange = (value: string) => {
    if (value.length > 100) {
      setInputBoxStatus(lang.componentTagModal.characterLimitAlert);
      setComponentTagName(value);

      return;
    }
    ALLOWED_REGEX.test(value)
      ? setInputBoxStatus('')
      : setInputBoxStatus(lang.componentTagModal.invalidTagAlert);
    setComponentTagName(value);
  };

  const handleComponentCheckboxChange = useCallback(
    (component: IComponent) => {
      const isSelected = selectedComponents.some(
        (selectedComponent) => selectedComponent.id === component.id,
      );

      const updatedSelectedComponents = isSelected
        ? selectedComponents.filter(
            (selectedComponent) => selectedComponent.id !== component.id,
          )
        : [...selectedComponents, component];

      setSelectedComponents(updatedSelectedComponents);
      setSelectAll(
        areAllComponentsSelected(components, updatedSelectedComponents),
      );
    },
    [components, setSelectedComponents, selectedComponents, setSelectAll],
  );

  const areAllComponentsSelected = (
    components: IComponent[],
    selected: IComponent[],
  ) => {
    return components.every((component) =>
      selected.some(
        (selectedComponent) => selectedComponent.id === component.id,
      ),
    );
  };
  const handleModalConfirm = () => {
    navigate(PATHS.COMPONENT_TAGS_MANAGE);
  };

  const handleDone = async () => {
    try {
      if (componentTagName) {
        const request = {
          name: componentTagName,
        };
        const result = await createComponentTag.mutateAsync(request);

        if (result.id) {
          const updatedComponents = selectedComponents.map((component) => ({
            ...component,
            component_tag_id: concat(component.component_tag_id, [
              Number(result.id),
            ]),
          }));

          await updateComponent.mutateAsync(updatedComponents);
          navigate(PATHS.COMPONENT_TAGS_MANAGE);
          Snackbar.success(`${componentTagName} ${lang.successfullyCreated}`);
        }
      }
    } catch (error: any) {
      if (
        error.response?.data?.error?.includes(
          lang.componentTagModal.uniqueTagAlert,
        )
      ) {
        setInputBoxStatus(lang.componentTagModal.uniqueTagAlert);
      }
      Snackbar.error(lang.failedToCreateTag);
    }
  };

  const totalCount = data?.pages?.[0]?.totalCount ?? 0;

  const numberofResults =
    data?.pages?.reduce((total, page) => {
      return total + (page.data?.length || 0);
    }, 0) || 0;

  const shouldDisplayResultInfo = numberofResults != null && totalCount != null;
  const resultCountText = `${lang.Showing} ${numberofResults}/${totalCount}`;

  return (
    <Grid>
      <Header
        backLink={-1}
        title={lang.createNewComponentTag}
        breadcrumbItems={[
          {
            label: lang.components,
            link: `${PATHS.COMPONENTS}`,
          },
          {
            label: lang.tags,
            link: `${PATHS.COMPONENT_TAGS_MANAGE}`,
          },
          {
            label: lang.createTag,
          },
        ]}
        actionBar={isViewOnly && <ViewOnlyAlert />}
      />
      <Grid container spacing={4}>
        <Grid item xs={12} md={3}>
          <FilterList
            filters={filters}
            onChangeKeyword={onChangeKeyword}
            onRemoveKeyword={onRemoveKeyword}
            onChangeFilter={onChangeParams}
            showReset
            onReset={onReset}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <Typography variant="h4" component="h2" gutterBottom>
            Create tag
          </Typography>
          <StyleDivider />
          <InputContainer>
            <InputTextLabel>{lang.tagName}</InputTextLabel>
            <InputTextField
              variant="outlined"
              inputProps={{
                maxLength: tagNameMaxLength,
                'data-testid': TestIds.TAG_NAME_INPUT,
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleComponentTagNameChange(event.target.value)
              }
              onBlur={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleComponentTagNameChange(event.target.value.trim())
              }
              value={componentTagName}
              error={!!inputBoxStatus}
              helperText={inputBoxStatus}
            />
            {shouldDisplayResultInfo && (
              <StyledTypography
                paddingTop={4}
                data-testid={DataTestID.ComponentTagCount}>
                {resultCountText}
              </StyledTypography>
            )}
          </InputContainer>
          <ComponentContainer>
            {components.length > 0 && (
              <ComponentSectionHeader>
                <Typography variant="h4" component="h2" flexGrow={1}>
                  {lang.selectComponents}
                </Typography>

                <Typography
                  variant="body1"
                  color="grey"
                  marginRight={1}
                  marginBottom={0}
                  paddingTop={1}>
                  {`${selectedCount} Selected`}
                </Typography>
                <Button variant="text" onClick={handleSelectAllClick}>
                  {selectAll ? lang.deselectAll : lang.selectAll}
                </Button>
              </ComponentSectionHeader>
            )}
            <StyleDivider />
            {isLoadingComponents ? (
              <Progress />
            ) : (
              <CardGridWrapper>
                <NoResultAlert
                  isLoading={isLoadingComponents}
                  dataToCheck={components}
                />
                <CardGrid
                  isLoading={isLoadingComponents}
                  error={errorLoadingComponents}
                  updatePage={getNextComponentPage}>
                  {components.map((component: IComponent) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      key={component.id}
                      data-testid={DataTestID.AddComponentTagComponentData}>
                      <CheckboxLabel
                        label={`${component.name}`}
                        control={
                          <Checkbox
                            checked={selectedComponents.some(
                              (selectedComponent) =>
                                selectedComponent.id === component.id,
                            )}
                            onChange={() =>
                              handleComponentCheckboxChange(component)
                            }
                          />
                        }
                      />
                    </Grid>
                  ))}
                </CardGrid>
                <div ref={gridBottomRef}></div>
              </CardGridWrapper>
            )}
          </ComponentContainer>
        </Grid>
      </Grid>
      <SimpleDialog
        open={CloseModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.discardModal.title}
        onClose={CloseModal.onCloseModal}
        onClickCancel={CloseModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={handleModalConfirm}
      />
      {!isViewOnly && (
        <Footer
          showFooterActions
          onCancel={CloseModal.onOpenModal}
          onSave={handleDone}
          cancelLabel={lang.close}
          saveLabel={lang.create}
          disableSave={
            componentTagName === '' || inputBoxStatus !== ''
          }></Footer>
      )}
    </Grid>
  );
};
